/**
 * Chamado para o departamento de Sistemas, aberto de dentro do Supply.
 *
 * O chamado é do Portal de Chamados (pc.flexibase.com.br, repo
 * Flexibase-Projects/PC): departamento "Sistemas", template "Chamado para
 * Sistemas", sistema "SUPPLY (supply.flexibase.com.br)". O PC repassa ao Hub
 * SISTEMAS sozinho. Aqui fica o ESPELHO do formulário do PC
 * (`frontend/src/features/tickets/CreateTicketPage.tsx` e
 * `frontend/src/utils/validation.ts` de lá) — mudou lá, mude aqui e o teste.
 *
 * O departamento, o tipo e o sistema já vêm escolhidos, então o funil começa
 * no "Solicitante", como o PC faz quando o departamento tem um tipo só.
 */

/** Opção do campo "Sistema" (`hub_sistema`) do template do PC. */
export const SUPPORT_SYSTEM_OPTION = 'SUPPLY (supply.flexibase.com.br)'
export const SUPPORT_SYSTEM_NAME = 'SUPPLY'
export const SUPPORT_DEPARTMENT = 'Sistemas'
export const SUPPORT_PORTAL_URL = 'https://pc.flexibase.com.br'

export type SupportStep = 'voce' | 'motivo' | 'detalhes' | 'revisar'

export const SUPPORT_STEPS: readonly SupportStep[] = ['voce', 'motivo', 'detalhes', 'revisar']

export const SUPPORT_STEP_TITLE: Record<SupportStep, string> = {
  voce: 'Solicitante',
  motivo: 'Qual é o motivo do chamado?',
  detalhes: 'Detalhes da solicitação',
  revisar: 'Confira e envie',
}

/**
 * Motivos do template do PC (campo `hub_motivo`), com a chave que o PC usa
 * para o Hub (`HUB_REASONS`). O PC devolve a lista viva em
 * `/formulario`; esta é a reserva e o mapa chave → rótulo.
 */
export const SUPPORT_REASONS = [
  { key: 'erro', label: 'Erro ou algo não funciona' },
  { key: 'duvida', label: 'Dúvida de uso' },
  { key: 'acesso', label: 'Acesso ou senha' },
  { key: 'melhoria', label: 'Pedido de melhoria' },
  { key: 'novo_sistema', label: 'Pedido de sistema novo' },
  { key: 'outro', label: 'Outro' },
] as const

/** "Pedido de sistema novo" esconde o campo Sistema no PC. */
export const SUPPORT_NEW_SYSTEM_LABEL = 'Pedido de sistema novo'

/** Chave ('erro') ou rótulo → rótulo do PC; desconhecido volta como veio. */
export function supportReasonLabel(value: string): string {
  const v = value.trim()
  return SUPPORT_REASONS.find((r) => r.key === v || r.label === v)?.label ?? v
}

export const SUPPORT_TITLE_MAX = 150

export interface SupportForm {
  readonly name: string
  /** E-mail do SSO: o PC usa o do token, aqui só aparece. */
  readonly email: string
  readonly extension: string
  /** Rótulo do motivo (ou a chave, vinda do pré-preenchimento). */
  readonly reason: string
  /** "Assunto do chamado". */
  readonly title: string
  /** "Detalhes". */
  readonly description: string
}

export type SupportErrors = Partial<Record<keyof SupportForm, string>>

/** Validação de uma etapa — as mensagens do formulário do PC. */
export function validateSupportStep(step: SupportStep, form: SupportForm, detailsLabel = 'Detalhes'): SupportErrors {
  const errors: SupportErrors = {}
  if (step === 'voce') {
    if (!form.name.trim()) errors.name = 'Nome é obrigatório'
    if (form.extension && !/^\d+$/.test(form.extension)) errors.extension = 'Ramal deve ser um número'
  }
  if (step === 'motivo' && !form.reason.trim()) errors.reason = 'Qual é o motivo do chamado? é obrigatório'
  if (step === 'detalhes') {
    if (!form.title.trim()) errors.title = 'Assunto do chamado é obrigatório'
    else if (form.title.trim().length > SUPPORT_TITLE_MAX) errors.title = `Assunto com até ${SUPPORT_TITLE_MAX} caracteres`
    if (!form.description.trim()) errors.description = `${detailsLabel} é obrigatório`
  }
  return errors
}

/** Primeira etapa com problema, ou `null` quando o formulário inteiro passa. */
export function firstInvalidSupportStep(form: SupportForm): SupportStep | null {
  return SUPPORT_STEPS.find((s) => Object.keys(validateSupportStep(s, form)).length > 0) ?? null
}
